import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import BlogContentSection from "../../components/HomePage/BlogContentStyle";
import SocialShare from "./SocialShare";
import { Helmet } from "react-helmet";
import PulseLoader from "../../components/Shared/Loading";
import { useSecureFetch } from "../../hooks/useSecureFetch";

const BlogDetails = () => {
  const [isInitializing, setIsInitializing] = useState(true);
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [author, setAuthor] = useState(null);
  const [error, setError] = useState(null);
  const { secureFetch, isLoading } = useSecureFetch();

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        const [blogResponse, authorResponse] = await Promise.all([
          axios.get(`https://server.enjoywiki.com/flip-server/blog/${slug}`),
          secureFetch("https://server.enjoywiki.com/flip-server/authors"),
        ]);

        if (!isMounted) return;

        const blogData = blogResponse.data;

        if (!blogData || !blogData._id) {
          throw new Error("Invalid blog data received");
        }

        const authorData = authorResponse.find(
          (author) => author.authorEmail === blogData.authorEmail
        );

        setBlog(blogData);
        setAuthor(authorData || null);
      } catch (err) {
        if (isMounted) {
          console.error("Error fetching data:", err);
          setError("Failed to load blog post. Please try again later.");
        }
      } finally {
        if (isMounted) {
          setIsInitializing(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [slug]);

  const formatDate = (isoString) => {
    return new Date(isoString).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const getMetaDescription = (content) => {
    if (!content) return "";
    // Strip HTML tags and get first 155 characters for meta description
    return content.replace(/<[^>]+>/g, "").slice(0, 155) + "...";
  };

  if (isInitializing) {
    return <PulseLoader />;
  }

  if (error) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: "#f5f5f5",
        }}
      >
        <div
          style={{
            padding: "2rem",
            background: "#ffffff",
            borderRadius: "8px",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "1rem",
            minWidth: "300px",
            border: "1px solid #e5e5e5",
            transition: "transform 0.2s, box-shadow 0.2s",
            transform: "translateY(0)",
            cursor: "default",
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = "translateY(-5px)";
            e.currentTarget.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.15)";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.1)";
          }}
        >
          <h1
            style={{
              fontSize: "1.5rem",
              fontWeight: "700",
              color: "#dc2626",
              marginBottom: "0.5rem",
            }}
          >
            {error}
          </h1>

          <p
            style={{
              color: "#4b5563",
              marginBottom: "1rem",
            }}
          >
            Please try again later.
          </p>

          <Link
            to="/blog"
            style={{
              color: "#ffffff",
              backgroundColor: "#3b82f6",
              padding: "0.5rem 1rem",
              borderRadius: "4px",
              textDecoration: "none",
              transition: "all 0.2s ease",
              border: "1px solid transparent",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = "#2563eb";
              e.currentTarget.style.boxShadow =
                "0 2px 4px rgba(59, 130, 246, 0.3)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = "#3b82f6";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            Return to Blogs
          </Link>
        </div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">No blog post found</h2>
          <Link to="/blogs" className="text-blue-500 hover:underline">
            Return to Blogs
          </Link>
        </div>
      </div>
    );
  }

  const canonicalUrl = `https://flipbundle.com/blog/${slug}`;
  const metaDescription = getMetaDescription(blog.blogContent);
  const publishDate = new Date(blog.createdAt).toISOString();
  const modifiedDate = new Date(blog.updatedAt || blog.createdAt).toISOString();

  return (
    <>
      <Helmet>
        <title>{`${blog.blogTitle} | Flipbundle`}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph tags */}
        <meta property="og:site_name" content="Flipbundle" />
        <meta property="og:title" content={blog.blogTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={blog.featuredImage} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:locale" content="en_US" />

        {/* Article specific tags */}
        <meta property="article:published_time" content={publishDate} />
        <meta property="article:modified_time" content={modifiedDate} />
        {blog.categories?.map((category) => (
          <meta property="article:section" content={category} key={category} />
        ))}
        {blog.tags?.map((tag) => (
          <meta property="article:tag" content={tag} key={tag} />
        ))}

        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@flipbundle" />
        <meta name="twitter:title" content={blog.blogTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={blog.featuredImage} />

        {/* Additional SEO tags */}
        <meta name="author" content={blog.authorName} />
        <meta name="robots" content="index, follow" />

        {/* Schema.org markup for Google */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": canonicalUrl,
            },
            headline: blog.blogTitle,
            image: blog.featuredImage,
            datePublished: publishDate,
            dateModified: modifiedDate,
            author: {
              "@type": "Person",
              name: blog.authorName,
            },
            publisher: {
              "@type": "Organization",
              name: "Flipbundle",
              logo: {
                "@type": "ImageObject",
                url: "https://flipbundle.com/logo.png",
              },
            },
            description: metaDescription,
          })}
        </script>
      </Helmet>
      <section
        className="w-breadcrumb-area"
        style={{ position: "relative", overflow: "hidden" }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `url(${blog.featuredImage}) no-repeat center center/cover`,
            filter: "blur(5px)",
            zIndex: -1,
          }}
        ></div>
        <div className="container">
          <div className="row">
            <div className="col-auto">
              <div
                className="position-relative z-2 aos-init aos-animate"
                data-aos="fade-up"
                data-aos-duration={1000}
                data-aos-easing="linear"
              >
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb w-breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/">Home</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to="/blog">Blog</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      {blog.blogTitle}
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="py-110 bg-offWhite">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-xl-8">
              <h1 className="fw-bold text-dark-300 blog-details-title">
                {blog.blogTitle}
              </h1>
              <div className="blog-details-meta d-flex flex-wrap gap-3 gap-md-5 my-4 align-items-center">
                <p className="d-flex gap-3 align-items-center text-dark-200 fst-italic">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width={17}
                    height={20}
                    viewBox="0 0 17 20"
                    fill="none"
                  >
                    <path
                      d="M0.5184 19.9966C0.108036 19.8303 -0.0123965 19.4927 0.000984899 19.028C0.0366687 17.9127 0.183864 16.8267 0.576386 15.7945C1.20977 14.1264 2.31151 12.9671 3.80131 12.2235C4.87182 11.6903 6.00924 11.4408 7.17343 11.3332C8.26625 11.2305 9.3546 11.25 10.443 11.4066C11.8391 11.6071 13.1683 12.0376 14.337 12.9426C15.6706 13.9748 16.4691 15.4032 16.7902 17.1398C16.9062 17.761 16.924 18.4018 16.9909 19.0378C17.04 19.4976 16.8928 19.84 16.478 20.0064C11.1566 19.9966 5.83975 19.9966 0.5184 19.9966Z"
                      fill="#22BE0D"
                    />
                    <path
                      d="M12.9128 5.09648C12.8593 6.70099 12.2795 8.14407 11.035 9.23494C9.71022 10.3943 7.70747 10.5215 6.30242 9.48442C4.57176 8.20277 3.89822 6.37813 4.14801 4.13769C4.29967 2.79734 4.88845 1.70647 5.8876 0.909102C6.85106 0.14109 7.93941 -0.118175 9.10806 0.048146C10.3704 0.229142 11.3874 0.894427 12.1144 2.04889C12.672 2.92452 12.9039 3.91756 12.9128 5.09648Z"
                      fill="#22BE0D"
                    />
                  </svg>
                  By {blog.authorName}
                </p>
                <p className="d-flex gap-3 align-items-center text-dark-200 fst-italic">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M19.9882 3.62212C19.9882 5.86393 19.9882 8.10573 19.9882 10.3475C19.9344 10.2986 19.8855 10.2496 19.8318 10.2056C19.4259 9.84337 19.025 9.47137 18.6094 9.11894C18.4823 9.01126 18.4187 8.92315 18.4236 8.75184C18.4383 8.28683 18.4285 7.81694 18.4285 7.34704C12.7958 7.34704 7.18755 7.34704 1.56464 7.34704C1.56464 7.43025 1.56464 7.50367 1.56464 7.5722C1.56464 10.8811 1.56464 14.1899 1.56464 17.4988C1.56464 18.1351 1.85801 18.4337 2.49364 18.4337C4.513 18.4337 6.52747 18.4435 8.54683 18.4239C8.87931 18.419 9.10912 18.4875 9.24602 18.7959C9.27536 18.8595 9.32914 18.9134 9.37804 18.9672C9.70074 19.3098 10.0283 19.6574 10.351 20C7.59338 20 4.8357 20 2.07314 20C1.99002 19.9804 1.91179 19.9608 1.82867 19.9413C0.728534 19.6769 0.00488949 18.7567 0 17.6114C0 15.4919 0 13.3676 0 11.2433C0 8.80568 0 6.37298 0 3.93539C0 2.79001 0.723644 1.85022 1.82867 1.62017C2.22472 1.53696 2.64032 1.57122 3.05104 1.55164C3.12438 1.54674 3.19773 1.55164 3.29063 1.55164C3.29063 1.01322 3.29063 0.509055 3.29063 0C3.8138 0 4.32231 0 4.84548 0C4.84548 0.518845 4.84548 1.02301 4.84548 1.53696C8.28279 1.53696 11.7054 1.53696 15.1525 1.53696C15.1525 1.01811 15.1525 0.504161 15.1525 0C15.6855 0 16.194 0 16.7172 0C16.7172 0.518845 16.7172 1.0279 16.7172 1.55164C17.0545 1.55164 17.3772 1.54185 17.7048 1.55164C18.4578 1.58101 19.0641 1.89917 19.5286 2.49633C19.7829 2.82917 19.9002 3.22075 19.9882 3.62212ZM15.1525 3.12775C11.7005 3.12775 8.2779 3.12775 4.8357 3.12775C4.8357 3.6466 4.8357 4.15076 4.8357 4.66471C4.31253 4.66471 3.80402 4.66471 3.27107 4.66471C3.27107 4.14097 3.27107 3.63191 3.27107 3.11307C2.95814 3.11307 2.66477 3.10817 2.3714 3.11307C1.88734 3.12286 1.56464 3.44102 1.55975 3.9256C1.55486 4.40039 1.55975 4.87518 1.55975 5.34998C1.55975 5.48213 1.55975 5.61919 1.55975 5.75135C7.19244 5.75135 12.8056 5.75135 18.4236 5.75135C18.4236 5.10524 18.4334 4.47381 18.4187 3.84239C18.4089 3.47528 18.1351 3.15712 17.7684 3.12286C17.4212 3.09349 17.0692 3.11796 16.7025 3.11796C16.7025 3.6417 16.7025 4.15076 16.7025 4.65981C16.1744 4.65981 15.6708 4.65981 15.1525 4.65981C15.1525 4.15076 15.1525 3.6466 15.1525 3.12775Z"
                      fill="#22BE0D"
                    ></path>
                    <path
                      d="M14.3357 19.9972C13.8272 19.8749 13.2943 19.8112 12.8102 19.6203C11.2065 18.984 10.1161 17.824 9.67116 16.1549C9.09909 14.0354 9.65649 12.1901 11.2994 10.7315C12.4924 9.6693 13.9201 9.26303 15.4994 9.51266C17.3965 9.81124 18.7509 10.8685 19.5381 12.6062C19.7533 13.0761 19.8217 13.6145 19.9586 14.1186C19.9782 14.1872 19.9831 14.2557 19.9978 14.3242C19.9978 14.5836 19.9978 14.8431 19.9978 15.1074C19.9293 15.45 19.8951 15.8024 19.7924 16.1353C19.3035 17.7897 18.262 18.9693 16.6582 19.6106C16.1693 19.8063 15.6314 19.87 15.118 19.9972C14.854 19.9972 14.5949 19.9972 14.3357 19.9972ZM14.722 18.4358C16.7609 18.4358 18.4331 16.7618 18.4331 14.7207C18.4282 12.6845 16.7707 11.0203 14.7367 11.0105C12.6978 11.0007 11.0207 12.6698 11.0158 14.7158C11.0109 16.7569 12.6831 18.4309 14.722 18.4358Z"
                      fill="#22BE0D"
                    ></path>
                    <path
                      d="M2.97656 10.5301C2.97656 10.0113 2.97656 9.50713 2.97656 8.98828C3.49026 8.98828 3.99418 8.98828 4.51277 8.98828C4.51277 9.49244 4.51277 10.0064 4.51277 10.5301C4.00885 10.5301 3.50005 10.5301 2.97656 10.5301Z"
                      fill="#22BE0D"
                    ></path>
                    <path
                      d="M7.64364 8.98828C7.64364 9.50713 7.64364 10.0113 7.64364 10.5252C7.12505 10.5252 6.61625 10.5252 6.09766 10.5252C6.09766 10.0113 6.09766 9.50713 6.09766 8.98828C6.60646 8.98828 7.11527 8.98828 7.64364 8.98828Z"
                      fill="#22BE0D"
                    ></path>
                    <path
                      d="M10.7706 8.98828C10.7706 9.50713 10.7706 10.0113 10.7706 10.5252C10.2569 10.5252 9.75297 10.5252 9.23438 10.5252C9.23438 10.0211 9.23438 9.51202 9.23438 8.98828C9.73829 8.98828 10.2471 8.98828 10.7706 8.98828Z"
                      fill="#22BE0D"
                    ></path>
                    <path
                      d="M2.97656 13.6502C2.97656 13.1363 2.97656 12.6272 2.97656 12.1133C3.49515 12.1133 4.00396 12.1133 4.52255 12.1133C4.52255 12.6272 4.52255 13.1314 4.52255 13.6502C4.01374 13.6502 3.50494 13.6502 2.97656 13.6502Z"
                      fill="#22BE0D"
                    ></path>
                    <path
                      d="M6.09766 13.6551C6.09766 13.1363 6.09766 12.6321 6.09766 12.1133C6.61135 12.1133 7.11527 12.1133 7.63386 12.1133C7.63386 12.6174 7.63386 13.1314 7.63386 13.6551C7.12995 13.6551 6.62114 13.6551 6.09766 13.6551Z"
                      fill="#22BE0D"
                    ></path>
                    <path
                      d="M2.97656 15.2383C3.49515 15.2383 3.99907 15.2383 4.51277 15.2383C4.51277 15.7522 4.51277 16.2564 4.51277 16.7752C4.00885 16.7752 3.50005 16.7752 2.97656 16.7752C2.97656 16.2711 2.97656 15.762 2.97656 15.2383Z"
                      fill="#22BE0D"
                    ></path>
                    <path
                      d="M7.64364 15.25C7.64364 15.764 7.64364 16.2681 7.64364 16.787C7.12505 16.787 6.61625 16.787 6.09766 16.787C6.09766 16.2779 6.09766 15.7688 6.09766 15.25C6.60157 15.25 7.11038 15.25 7.64364 15.25Z"
                      fill="#22BE0D"
                    ></path>
                    <path
                      d="M13.9219 12.1133C14.4402 12.1133 14.9438 12.1133 15.467 12.1133C15.467 12.7202 15.467 13.3174 15.467 13.9341C15.9119 13.9341 16.3422 13.9341 16.7871 13.9341C16.7871 14.4628 16.7871 14.9669 16.7871 15.4858C15.8434 15.4858 14.89 15.4858 13.9219 15.4858C13.9219 14.3698 13.9219 13.2489 13.9219 12.1133Z"
                      fill="#22BE0D"
                    ></path>
                  </svg>
                  {formatDate(publishDate)}
                </p>
              </div>
              <img
                src={blog.featuredImage}
                className="img-fluid w-100 my-4"
                alt={blog.blogTitle}
              />
              {blog?.blogContent && (
                <BlogContentSection content={blog.blogContent} />
              )}
              <SocialShare
                url={window.location.href}
                title={blog.blogTitle}
                description={
                  blog.blogContent?.replace(/<[^>]+>/g, "").slice(0, 200) +
                  "..."
                }
              />

              {author && (
                <div className="pt-5">
                  <div className="d-flex flex-column gap-4">
                    <div className="bg-white blog-single-comment">
                      <div className="d-flex flex-column flex-md-row gap-3 pb-3">
                        <div>
                          <img
                            src={
                              author.authorImage ||
                              "https://raw.githubusercontent.com/Shah-Limon/em-list/master/images-dash/profile.jpg"
                            }
                            className="rounded-circle"
                            width={64}
                            alt={`${author.authorName}'s profile`}
                          />
                        </div>
                        <div>
                          <div className="d-flex align-items-center justify-content-between">
                            <h4 className="text-18 fw-semibold text-dark-300">
                              {author.authorName}
                            </h4>
                          </div>
                          <p className="py-2 text-dark-200 fs-6">
                            {author.authorDescription}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default BlogDetails;
